import { CaseClient, EvidenceClient, EvidenceCreateFromExistingModel, EvidenceDetailModel, EvidenceMarkerModel, KitClient, KitDetailModel, LabClient, LabKitClient } from '@app/api2/api';
import { Field, FormikProps } from 'formik';
import * as React from 'react';
import * as yup from "yup";
import { Checkbox, Container, Form } from 'semantic-ui-react';
import { BPFormikFieldCheckbox } from '@common/BPCheckbox';
import { BPColumn, BPReactTable } from '@common/BPReactTable';
import { InfoBox } from '@common/InfoBox';
import { PageHeader } from '@common/PageHeader';
import { insertComponentsIntoText, normalizeSystemName } from '@common/Utils';
import { BPForm } from '@common/BPForm';
import { RefObject } from 'react';
import { BPTextInput } from '@common/BPTextInput';
import SemanticField from '@app/components/SemanticField';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { BPCheckbox } from '@/components/bp/bpcheckbox';
import { BPContainer } from '@/components/bp/bpcontainer';

export interface IEvidenceCopyProps {
    evidenceId: number;
    labId: number;
    caseId: number;
}

export interface IEvidenceCopyState {
    evidence: EvidenceDetailModel;
    normalizeLSAE: boolean;
    selectAll: boolean;
    detailsUrl: string;
    caseUrl: string;
    kitDetail: KitDetailModel;
    formData: EvidenceCreateFromExistingModel;
}

export default class EvidenceCopy extends React.Component<IEvidenceCopyProps, IEvidenceCopyState> {
    private caseClient = new CaseClient();
    private labClient = new LabClient();
    private kitClient = new KitClient();

    private evidenceClient = new EvidenceClient();
    private formikRef: React.Ref<BPForm<EvidenceCreateFromExistingModel>>;
    private createSchema: yup.SchemaOf<EvidenceCreateFromExistingModel> = yup.object().shape({
        normalizeLSAE: yup.boolean(),
        name: yup.string().required().label(window.translation.common.name),
        markers: yup.array().of(yup.object().shape({
            name: yup.string().required(),
            lsae: yup.number().required(),
            isSelected: yup.boolean().required(),
        })).min(1).label(window.translation.kit.markers),
    });

    constructor(props: IEvidenceCopyProps) {
        super(props);
        this.formikRef = React.createRef();
        // this.formikRef.current.formik.current.setErrors()

        this.state = {
            selectAll: false,
            evidence: null,
            caseUrl: '',
            detailsUrl: '',
            normalizeLSAE: false,
            kitDetail: null,

            formData: {
                normalizeLSAE: false,
                name: '',
                markers: [],
            }
        }
    }

    async componentDidMount() {
        // const kits = await this.caseClient.getCaseKits(this.props.labId, this.props.caseId);
        const evidence = await this.evidenceClient.getDetails(this.props.labId, this.props.caseId, this.props.evidenceId);
        const markerSettings = await this.labClient.getKitMarkerSettings(this.props.labId, evidence.evidence.kitId);
        const markerSettings2 = await this.kitClient.get(evidence.evidence.kitId);

        // console.log(markerSettings);
        this.setState({
            selectAll: true,
            evidence: evidence.evidence,
            caseUrl: evidence.caseUrl,
            detailsUrl: evidence.detailsUrl,
            normalizeLSAE: false,
            kitDetail: markerSettings2,
            formData: {
                normalizeLSAE: false,
                name: evidence.evidence.name,
                markers: evidence.evidence.markers.map(m => {
                    return {
                        name: m.name,
                        lsae: markerSettings.find(ms => normalizeSystemName(ms.markerName) == normalizeSystemName(m.name))?.lsae ?? 1, isSelected: true
                    }
                }),
            }
        });
    }

    public render() {
        const selectAllCheckboxes = isSelected => {
            this.setState({
                ...this.state,
                selectAll: isSelected,
                formData: {
                    ...(this.formikRef as RefObject<any>).current.formik.current.values,
                    markers: (this.formikRef as RefObject<any>).current.formik.current.values.markers.map(x => { return { ...x, isSelected: isSelected } })
                }

            })
        };

        const columns: BPColumn<EvidenceMarkerModel>[] = [
            {
                id: 'checkbox',
                Header: <BPCheckbox checked={this.state.selectAll} onChange={() => selectAllCheckboxes(!this.state.selectAll)} />,
                Cell: cell => <BPFormikFieldCheckbox key={cell.row.original.name} name={`markers[${cell.row.index}].isSelected`} />,
                tableCellProps: {
                    collapsing: true,
                }
            },
            this.state.normalizeLSAE ? {
                id: 'lsae',
                Header: "LSAE",
                Cell: (cell: any): JSX.Element => <SemanticField disabled={this.state?.kitDetail?.systems.find(x => normalizeSystemName(x.name) == normalizeSystemName(cell.row.original.name))?.genderMarker == true} style={{ width: "60px" }} name={`markers[${cell.row.index}].lsae`} component={Form.Input} />,
                tableCellProps: {
                    collapsing: true,
                },
                hidden: this.state.normalizeLSAE,
            } : null,
            { accessor: 'name', Header: window.translation.kit.marker },
        ].filter(column => column !== null) as BPColumn<EvidenceMarkerModel>[];

        const otherColumns: BPColumn[] = [
            ...(this.state.evidence ? Array(this.state.evidence.maxAlleles).keys() : [])
        ].map(alleleIndex => {
            return [
                {
                    id: `al${alleleIndex}`,
                    accessor: 'alleles',
                    Header: window.translation.evidenceView.alleleWithNumber.replace('{alleleNumber}', (alleleIndex + 1).toString()),
                    Cell: cellProps => <span>{cellProps.value.length > alleleIndex ? cellProps.value[alleleIndex].allele : ''}</span>
                },
                {
                    id: `hi${alleleIndex}`,
                    accessor: 'alleles',
                    Header: window.translation.evidenceView.heightWithNumber.replace('{heightNumber}', (alleleIndex + 1).toString()),
                    Cell: cellProps => <span>{cellProps.value.length > alleleIndex ? cellProps.value[alleleIndex].height : ''}</span>
                }
            ]
        }).reduce((prev, curr) => { prev.push(curr[0], curr[1]); return prev; }, []);

        const cols = [...columns, ...otherColumns]
        const caseName = this.state?.evidence?.cases?.length > 0 ? this.state.evidence.cases[0].name : ''

        return (
            <>
                {this.state.evidence &&
                    <BPContainer>
                        <PageHeader header={insertComponentsIntoText(window.translation.evidence.createFromExistingTitle, {
                            '{caseName}': <a key='caseUrlLink' href={this.state.caseUrl}>{caseName}</a>
                        })} />
                        <BPForm
                            initialValues={this.state.formData}
                            ref={this.formikRef}
                            onCancel={() => {
                                window.location.assign(this.state.detailsUrl);
                            }}
                            vaildationSchema={this.createSchema}
                            onSubmit={(values) => this.save(values)}>
                            {(props) => (
                                <>
                                    <InfoBox
                                        items={[
                                            { label: window.translation.evidence.originalName, description: this.state.evidence.name },
                                            { label: window.translation.common.kit, description: <><span style={{ marginRight: "300px" }}>{this.state.evidence.kitName}</span><BPCheckbox checked={this.state.normalizeLSAE} onChange={(ev, data) => this.setState({ ...this.state, normalizeLSAE: data.checked, formData: { ...this.state.formData, normalizeLSAE: data.checked } })} label={window.translation.evidence.normalizeForLSAE} /></> },
                                            { label: window.translation.common.name, description: <Field as={Form.Field} control={Form.Input} name="name" error={props.errors.name && props.touched.name}></Field> },
                                        ]} />
                                    {/* <BPSection header={window.translation.evidence.selectMarkersForInclude}> */}
                                    <BPReactTable
                                        compact
                                        disablePagination
                                        disableGlobalFilter
                                        disableSortBy
                                        stickyHeader
                                        celled
                                        data={this.state.evidence.markers}
                                        columns={cols}
                                    />
                                    {/* </BPSection> */}
                                </>
                            )}
                        </BPForm>
                    </BPContainer>
                }
            </>
        );
    }

    async save(values: EvidenceCreateFromExistingModel) {
        // console.log(values);
        // return;

        let result = await this.evidenceClient.createFromExisting(this.props.labId, this.props.caseId, this.props.evidenceId, {
            normalizeLSAE: values.normalizeLSAE,
            name: values.name,
            markers: values.markers,
        })

        if (result.successful) {
            if (this.props.caseId == 0)
                window.location.assign(buildUrl(AppRoutes.EvidenceDetails, { labId: this.props.labId, caseId: this.props.caseId, evidenceId: result.newEvidence[0].id }))
            else
                window.location.assign(result.url)
        }
    }
}

